import {
  Container,
  Center,
  Loader,
  TextInput,
  Title,
  Paper,
  Button,
  Avatar,
  Group,
  Stack,
  Grid,
  SegmentedControl,
  Text,
  useMantineColorScheme,
} from "@mantine/core";
import { IconDeviceDesktop, IconMoon, IconSun } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useForm } from "@mantine/form";
import { getProfile, updateProfile, getTimezones, userLogout } from "../api";
import { useStore } from "../store";
import ImageUploader from "./ImageUploader";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import UserSessions from "../UserSessions";
import PasskeyRegister from "../Auth/PasskeyRegister";
import { IconPasswordUser } from "@tabler/icons-react";

export default function ProfileEdit() {
  const profile = useStore((state) => state.profile);
  const setProfile = useStore((state) => state.setProfile);
  const [, setTimezones] = useState([]);
  const { t } = useTranslation();
  const { colorScheme, setColorScheme } = useMantineColorScheme();

  const form = useForm({
    mode: "uncontrolled",
  });

  const interfaceLanguages = [
    { label: "English", value: "en" },
    { label: "Deutsch", value: "de" },
  ];

  // const form = useForm({
  //   initialValues: {
  //     name: "",
  //   },
  //   // validate: {
  //   //   // name: (val) => (/^[a-zA-Z0-9_-]{1, 22}$/.test(val) ? null : "Invalid plan name")
  //   // },
  // });

  useEffect(() => {
    getProfile().then((data) => {
      form.setValues(data);
      setProfile(data);
    });
    getTimezones().then((data) => {
      const mapped = data?.map((p) => ({ ...p, value: p.id, label: p.name }));
      if (mapped) setTimezones(mapped);
    });
  }, []);

  const updateProfileHandler = (data) => {
    updateProfile(data).then((response) => {
      notifications.show({
        color: "green",
        autoClose: 4000,
        title: t("settings.changes_saved"),
        message: t("settings.changes_saved_message"),
      });
    });
  };

  if (!profile)
    return (
      <Container size="sm">
        <Center>
          <Loader />
        </Center>
      </Container>
    );

  return (
    <Container size="sm">
      <Title my="xs" order={1}>
        {t("settings.profile")}
      </Title>

      <Grid>
        <Grid.Col pt="xl" span={{ base: 12, xs: 5 }}>
          <ImageUploader
            image={
              <Avatar
                src={profile.profileImageUrl}
                size={120}
                radius={120}
                mx="auto"
              />
            }
            message="Upload a portrait"
            imageType="profile"
            onProfileUpdated={(data) => setProfile(data)}
          />

          {(profile.role === "TenantOwner" ||
            profile.role === "TenantAdmin") && (
            <Button
              component={Link}
              to="/settings/tenant"
              variant="light"
              mt="md"
            >
              {t("settings.organization_settings")}
            </Button>
          )}
        </Grid.Col>
        <Grid.Col span={{ base: 12, xs: 7 }}>
          <Paper p="md" shadow={0}>
            <form onSubmit={form.onSubmit(updateProfileHandler)}>
              <Stack gap="sm">
                <TextInput
                  {...form.getInputProps("firstName")}
                  key={form.key("firstName")}
                  label={t("generic.first_name")}
                />
                <TextInput
                  {...form.getInputProps("lastName")}
                  key={form.key("lastName")}
                  label={t("generic.last_name")}
                />
                <TextInput
                  {...form.getInputProps("emailAddress")}
                  key={form.key("emailAddress")}
                  disabled
                  label={t("generic.email")}
                />
                <TextInput
                  {...form.getInputProps("role")}
                  key={form.key("role")}
                  disabled
                  label={t("generic.role")}
                />

                {/* <Select
                  label={t("settings.select_current_timezone")}
                  placeholder={t("settings.pick_timezone")}
                  data={timezones}
                  disabled={timezones.length === 0}
                  value={form.values.timeZone}
                  onChange={(_value) => {
                    form.setFieldValue("timeZone", _value);
                  }}
                  searchable
                />

                <Select
                  label={t("settings.select_interface_language")}
                  // placeholder={t("settings.pick_timezone")}
                  value={form.values.interfaceLanguage}
                  onChange={(_value) => {
                    form.setFieldValue("interfaceLanguage", _value);
                    i18n.changeLanguage(_value);
                  }}
                  data={interfaceLanguages}
                /> */}

                <Group mt="lg" justify="flex-end">
                  <Button variant="default" onClick={() => userLogout()}>
                    {t("generic.logout")}
                  </Button>
                  <Button type="submit">{t("generic.action_save")}</Button>
                </Group>
              </Stack>
            </form>
          </Paper>
        </Grid.Col>

        <Grid.Col span={12}>
          <Paper mt="xl" p="md">
            <Title mb="sm" order={3}>
              {t("settings.password", "Password")}
            </Title>

            <Group justify="center">
              {profile.logins?.find((p) => p.provider == "password") ? (
                <Button
                  variant="default"
                  component={Link}
                  leftSection={<IconPasswordUser size={18} />}
                  to="/auth/change-password"
                >
                  {t("settings.change_password", "Change password")}
                </Button>
              ) : (
                <Button
                  variant="default"
                  component={Link}
                  leftSection={<IconPasswordUser size={18} />}
                  to="/auth/set-password"
                >
                  {t("settings.set_password", "Set password")}
                </Button>
              )}
            </Group>
          </Paper>
        </Grid.Col>
        <Grid.Col span={12}>
          <Paper mt="xl" p="md">
            <Title mb="sm" order={3}>
              {t("settings.appearance", "Appearance")}
            </Title>
            <Stack gap="xs">
              <Text size="sm" c="dimmed">
                {t(
                  "settings.appearance_description",
                  "Choose a theme for the interface. System matches your device setting.",
                )}
              </Text>
              <SegmentedControl
                value={colorScheme}
                onChange={(v) => setColorScheme(v)}
                data={[
                  {
                    value: "light",
                    label: (
                      <Group gap={6} justify="center">
                        <IconSun size={16} />
                        <Text size="sm">
                          {t("settings.theme_light", "Light")}
                        </Text>
                      </Group>
                    ),
                  },
                  {
                    value: "dark",
                    label: (
                      <Group gap={6} justify="center">
                        <IconMoon size={16} />
                        <Text size="sm">
                          {t("settings.theme_dark", "Dark")}
                        </Text>
                      </Group>
                    ),
                  },
                  {
                    value: "auto",
                    label: (
                      <Group gap={6} justify="center">
                        <IconDeviceDesktop size={16} />
                        <Text size="sm">
                          {t("settings.theme_system", "System")}
                        </Text>
                      </Group>
                    ),
                  },
                ]}
              />
            </Stack>
          </Paper>
        </Grid.Col>
        <Grid.Col span={12}>
          <PasskeyRegister />
        </Grid.Col>
        {/* <Grid.Col span={12}>
          <Title order={3}>Login Methods</Title>
          <UserLogins user={profile} />
        </Grid.Col> */}
        <Grid.Col span={12}>
          <UserSessions user={profile} />
        </Grid.Col>
      </Grid>
    </Container>
  );
}
